import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Transition } from "lib/headlessui/components/transition/transition";
import React from "react";
import { Badge, BadgeProps } from "./badge";

export type ListSectionTone = "accent" | "alert";

export type ListSectionDisclosure =
    | { kind: "static" }
    | { kind: "collapsible"; open: boolean; onToggle: () => void };

interface ListSectionProps {
    label: string;
    count: number;
    tone: ListSectionTone;
    disclosure: ListSectionDisclosure;
    children: React.ReactNode;
}

const toneStyles: Record<
    ListSectionTone,
    { bar: string; label: string; badge: BadgeProps["type"] }
> = {
    accent: { bar: "bg-brand-500", label: "text-gray-800", badge: "info" },
    alert: { bar: "bg-red-500", label: "text-red-700", badge: "error" },
};

const cardClasses = "overflow-hidden rounded-lg bg-white ring-1 ring-gray-300";
const headerClasses = "flex w-full items-center gap-2 px-4 py-3";

export const ListSection: React.FC<ListSectionProps> = props => {
    const { label, count, tone, disclosure, children } = props;
    const styles = toneStyles[tone];

    const heading = (
        <>
            <span className={`h-5 w-1 flex-shrink-0 rounded-full ${styles.bar}`} />
            <span className={`text-sm font-semibold ${styles.label}`}>{label}</span>
            <Badge value={count} type={styles.badge} />
        </>
    );

    if (disclosure.kind === "static") {
        return (
            <div className={cardClasses}>
                <h3 className={`${headerClasses} border-b border-gray-200`}>{heading}</h3>
                {children}
            </div>
        );
    }

    return (
        <div className={cardClasses}>
            <button
                type="button"
                onClick={disclosure.onToggle}
                className={`${headerClasses} hover:bg-gray-50 ${
                    disclosure.open ? "border-b border-gray-200" : ""
                }`}>
                <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                        disclosure.open ? "rotate-0" : "-rotate-90"
                    }`}
                    aria-hidden="true"
                />
                {heading}
            </button>
            <Transition
                as="div"
                show={disclosure.open}
                enter="transition-opacity duration-100"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="transition-opacity duration-50"
                leaveFrom="opacity-100"
                leaveTo="opacity-0">
                {children}
            </Transition>
        </div>
    );
};
